import React from 'react';
import { BodyTextHtmlTagOptions } from '#utils/body-text-html-tag-options.ts';
import {
  HeadingText,
  ParagraphText,
  StackLayout,
  useClickableCard,
  styled,
  Image,
  StackItem,
  Icon,
  spacingTokenMap,
  type GDSHeadingSize,
  type GDSSpaceToken,
  type GDSAlign,
} from '@leagueplatform/genesis-core';
import {
  type MasonryEngineNode,
  type MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';
import {
  getIconToken,
  getLabelByIndicator,
  Indicator,
} from '#utils/icon-mapping.ts';
import { useUniqueId } from '@leagueplatform/web-common';
import { useInvokeAction } from '../../hooks/use-invoke-action.hook';

import { ConditionalLinkRenderer } from '../common/conditional-link-renderer/conditional-link-renderer.component';
import { ConditionalLinkCardRenderer } from '../common/conditional-link-card-renderer/conditional-link-card-renderer.component';
import {
  MasonryDeeplinkAction,
  MasonryDismissAction,
  MasonryDriverAction,
} from '../../types/masonry-driver-node-actions';
import { formatClassNames } from '../../utils/format-masonry-class-name.util';

export type MasonryMicroCardNodeProperties = {
  heading: string;
  body?: string;
  imageAltText: string;
  image: string;
  actionIndicator?: Indicator;
  actionIndicatorLabel?: string;
  headingSize?: GDSHeadingSize;
  padding?: GDSSpaceToken;
  alignment?: GDSAlign;
};

export type MasonryMicroCardNodeActions = {
  onClick?: MasonryDriverAction;
  onDismiss?: MasonryDismissAction;
};

export type MasonryMicroCardNode = MasonryEngineNode<
  'microCard',
  MasonryMicroCardNodeProperties,
  MasonryMicroCardNodeActions
>;

export type MasonryMicroCardRendererProps =
  MasonryEngineNodeRendererProps<MasonryMicroCardNode>;

const StyledImage = styled(Image, {
  width: '42px',
  height: '42px',
});

export const MasonryMicroCardRenderer = ({
  heading,
  body,
  imageAltText = '',
  image,
  actionIndicator,
  actionIndicatorLabel,
  onClick,
  onDismiss,
  className,
  engineClassName,
  locatorId,
  css,
  padding = 'one',
  headingSize = 'sm',
  alignment = 'start',
  rootElementRef,
}: MasonryMicroCardRendererProps) => {
  const { primaryActionRef, clickAreaRef, onCardMouseDown, onCardMouseUp } =
    useClickableCard(rootElementRef);

  const invokeAction = useInvokeAction();

  const isValidActionIcon = onClick && actionIndicator;
  const uniqueId = useUniqueId({ prefix: 'microCard' });
  const iconId = `${uniqueId}-icon`;

  const onClickAsDeeplinkAction = onClick as MasonryDeeplinkAction;

  return (
    <ConditionalLinkCardRenderer
      hasLink={Boolean(onClickAsDeeplinkAction?.payload.url)}
      ref={clickAreaRef}
      onCardMouseUp={onCardMouseUp}
      onCardMouseDown={onCardMouseDown}
      onDismiss={onDismiss}
      css={{ width: '100%', ...css }}
      className={formatClassNames(engineClassName, className)}
      data-testid={locatorId}
    >
      <StackLayout
        orientation="horizontal"
        verticalAlignment="center"
        spacing="$threeQuarters"
        css={{ padding: spacingTokenMap[padding] }}
      >
        <StackLayout
          orientation="vertical"
          spacing="$quarter"
          horizontalAlignment={alignment}
        >
          <StyledImage
            src={image}
            alt={imageAltText}
            loading="lazy"
            css={{ marginBottom: '$quarter' }}
          />

          <HeadingText size={headingSize} level={!body ? 'display' : '3'}>
            <ConditionalLinkRenderer
              disableDefaultOnClick
              ref={primaryActionRef}
              onClick={(event: React.MouseEvent) => {
                if (onClick) {
                  invokeAction({ event, ...onClick });
                }
              }}
              url={onClickAsDeeplinkAction?.payload.url}
            >
              {heading}
            </ConditionalLinkRenderer>
          </HeadingText>

          {body && (
            <ParagraphText size="sm" htmlStringOptions={BodyTextHtmlTagOptions}>
              {body}
            </ParagraphText>
          )}
        </StackLayout>
        {isValidActionIcon && (
          <StackItem verticalAlignment="center">
            <Icon
              icon={getIconToken(actionIndicator)}
              label={
                actionIndicatorLabel ?? getLabelByIndicator(actionIndicator)
              }
              id={iconId}
            />
          </StackItem>
        )}
      </StackLayout>
    </ConditionalLinkCardRenderer>
  );
};
